import type {
  MIOGCClientOptions, MIAuthStrategy, MIProxyConfigOptions,
  MIWMSCapabilities, MIWMSLayerInfo, MIWFSCapabilities, MIWFSFeatureTypeInfo,
  MIWMTSCapabilities, MIWMTSLayerInfo, MITileMatrixSet,
  MIWCSCapabilities, MIWCSCoverageInfo, MIOGCCapabilities,
  MIWMSGetMapParams, MIWFSGetFeatureParams, MIOGCItemsParams, MIOGCCollection,
  MIServerEntry,
} from './types';

function parseXML(text: string): Document {
  const parser = new DOMParser();
  return parser.parseFromString(text, 'text/xml');
}

function getTagText(el: Element, tagName: string): string {
  const node = el.getElementsByTagName(tagName)[0]
    || el.querySelector(`[localName="${tagName}"]`);
  return node?.textContent?.trim() || '';
}

function getAllByTag(el: Element | Document, tagName: string): Element[] {
  const results: Element[] = [];
  const nodes = el.getElementsByTagName(tagName);
  for (let i = 0; i < nodes.length; i++) results.push(nodes[i]);
  if (results.length === 0) {
    const byLocal = el.querySelectorAll(`[localName="${tagName}"]`);
    byLocal.forEach(n => results.push(n as Element));
  }
  return results;
}

export class MIOGCClient {
  private url: string;
  private auth?: MIAuthStrategy;
  private proxy?: MIProxyConfigOptions;
  private headers: Record<string, string>;
  private timeout: number;
  private version?: string;

  constructor(options: MIOGCClientOptions) {
    this.url = options.url.replace(/\/$/, '');
    this.auth = options.auth;
    this.proxy = options.proxy;
    this.headers = options.headers || {};
    this.timeout = options.timeout || 30000;
    this.version = options.version;
  }

  private buildUrl(params: Record<string, string>): string {
    const url = new URL(this.url);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    return url.toString();
  }

  private resolveUrl(targetUrl: string): string {
    if (this.proxy?.enabled) {
      if (this.proxy.mode === 'custom' && this.proxy.proxyUrl) {
        return `${this.proxy.proxyUrl}?url=${encodeURIComponent(targetUrl)}`;
      }
      return `/api/v2/proxy?url=${encodeURIComponent(targetUrl)}`;
    }
    return targetUrl;
  }

  private getHeaders(): Record<string, string> {
    const h: Record<string, string> = { ...this.headers };
    if (this.auth) {
      switch (this.auth.type) {
        case 'api-key':
          h[this.auth.apiKeyHeader || 'X-API-Key'] = this.auth.apiKey || '';
          break;
        case 'bearer':
          h['Authorization'] = `Bearer ${this.auth.token}`;
          break;
        case 'basic':
          h['Authorization'] = `Basic ${btoa(`${this.auth.username}:${this.auth.password}`)}`;
          break;
      }
    }
    return h;
  }

  private async fetchXml(url: string): Promise<Document> {
    const resolvedUrl = this.resolveUrl(url);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeout);
    const response = await fetch(resolvedUrl, {
      headers: this.getHeaders(),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const text = await response.text();
    return parseXML(text);
  }

  private async fetchJson(url: string): Promise<any> {
    const resolvedUrl = this.resolveUrl(url);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeout);
    const response = await fetch(resolvedUrl, {
      headers: { ...this.getHeaders(), 'Accept': 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    return response.json();
  }

  async getCapabilities(service: 'WMS' | 'WFS' | 'WMTS' | 'WCS' | 'CSW'): Promise<MIOGCCapabilities> {
    switch (service) {
      case 'WMS': return this.getWMSCapabilities();
      case 'WFS': return this.getWFSCapabilities();
      case 'WMTS': return this.getWMTSCapabilities();
      case 'WCS': return this.getWCSCapabilities();
      default: throw new Error(`Service ${service} not yet supported`);
    }
  }

  async getWMSCapabilities(): Promise<MIWMSCapabilities> {
    const url = this.buildUrl({
      SERVICE: 'WMS',
      REQUEST: 'GetCapabilities',
      VERSION: this.version || '1.3.0',
    });
    const doc = await this.fetchXml(url);

    const version = doc.documentElement.getAttribute('version') || '1.3.0';
    const serviceEl = getAllByTag(doc, 'Service')[0];
    const title = serviceEl ? getTagText(serviceEl, 'Title') : '';
    const abstract = serviceEl ? getTagText(serviceEl, 'Abstract') : '';

    const layers = this.parseWMSLayers(doc);

    const formats: string[] = [];
    const getMapEl = getAllByTag(doc, 'GetMap')[0];
    if (getMapEl) {
      getAllByTag(getMapEl, 'Format').forEach(f => {
        if (f.textContent) formats.push(f.textContent.trim());
      });
    }

    const crs: string[] = [];
    getAllByTag(doc, 'CRS').forEach(c => {
      if (c.textContent) crs.push(c.textContent.trim());
    });
    if (crs.length === 0) {
      getAllByTag(doc, 'SRS').forEach(c => {
        if (c.textContent) crs.push(c.textContent.trim());
      });
    }

    return {
      service: 'WMS',
      version,
      title,
      abstract,
      layers,
      formats: formats.length > 0 ? formats : ['image/png', 'image/jpeg'],
      crs: Array.from(new Set(crs)),
      exceptions: ['XML'],
    };
  }

  private parseWMSLayers(doc: Document): MIWMSLayerInfo[] {
    const layers: MIWMSLayerInfo[] = [];
    const layerEls = getAllByTag(doc, 'Layer');

    layerEls.forEach(el => {
      const name = getTagText(el, 'Name');
      if (!name) return;

      const layerCrs: string[] = [];
      for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i];
        if (child.tagName === 'CRS' || child.tagName === 'SRS') {
          if (child.textContent) layerCrs.push(child.textContent.trim());
        }
      }

      const styles: { name: string; title: string; legendUrl?: string }[] = [];
      const styleEls = el.querySelectorAll(':scope > Style');
      styleEls.forEach(s => {
        styles.push({
          name: getTagText(s as Element, 'Name'),
          title: getTagText(s as Element, 'Title'),
        });
      });

      const bbox: { crs: string; extent: [number, number, number, number] }[] = [];
      const bboxEls = getAllByTag(el, 'BoundingBox');
      bboxEls.forEach(b => {
        const bCrs = b.getAttribute('CRS') || b.getAttribute('SRS') || 'EPSG:4326';
        const minx = parseFloat(b.getAttribute('minx') || '0');
        const miny = parseFloat(b.getAttribute('miny') || '0');
        const maxx = parseFloat(b.getAttribute('maxx') || '0');
        const maxy = parseFloat(b.getAttribute('maxy') || '0');
        bbox.push({ crs: bCrs, extent: [minx, miny, maxx, maxy] });
      });

      const queryable = el.getAttribute('queryable') === '1';

      layers.push({
        name,
        title: getTagText(el, 'Title') || name,
        abstract: getTagText(el, 'Abstract'),
        crs: layerCrs,
        bbox,
        styles,
        queryable,
      });
    });

    return layers;
  }

  async getWFSCapabilities(): Promise<MIWFSCapabilities> {
    const url = this.buildUrl({
      SERVICE: 'WFS',
      REQUEST: 'GetCapabilities',
      VERSION: this.version || '2.0.0',
    });
    const doc = await this.fetchXml(url);

    const version = doc.documentElement.getAttribute('version') || '2.0.0';
    const serviceEl = getAllByTag(doc, 'ServiceIdentification')[0] || getAllByTag(doc, 'Service')[0];
    const title = serviceEl ? getTagText(serviceEl, 'Title') : '';

    const featureTypes: MIWFSFeatureTypeInfo[] = [];
    const ftEls = getAllByTag(doc, 'FeatureType');
    ftEls.forEach(ft => {
      const name = getTagText(ft, 'Name');
      if (!name) return;

      const defaultCRS = getTagText(ft, 'DefaultCRS') || getTagText(ft, 'DefaultSRS') || 'EPSG:4326';
      const otherCRS: string[] = [];
      getAllByTag(ft, 'OtherCRS').forEach(c => {
        if (c.textContent) otherCRS.push(c.textContent.trim());
      });

      let bbox: [number, number, number, number] = [0, 0, 0, 0];
      const wgsEl = getAllByTag(ft, 'WGS84BoundingBox')[0] || getAllByTag(ft, 'LatLongBoundingBox')[0];
      if (wgsEl) {
        const lower = getTagText(wgsEl, 'LowerCorner').split(' ').map(Number);
        const upper = getTagText(wgsEl, 'UpperCorner').split(' ').map(Number);
        if (lower.length >= 2 && upper.length >= 2) {
          bbox = [lower[0], lower[1], upper[0], upper[1]];
        }
      }

      const outputFormats: string[] = [];
      getAllByTag(ft, 'Format').forEach(f => {
        if (f.textContent) outputFormats.push(f.textContent.trim());
      });

      featureTypes.push({
        name,
        title: getTagText(ft, 'Title') || name,
        abstract: getTagText(ft, 'Abstract'),
        defaultCRS,
        otherCRS,
        bbox,
        outputFormats,
      });
    });

    return {
      service: 'WFS',
      version,
      title,
      featureTypes,
      outputFormats: ['application/json', 'application/gml+xml'],
      filterCapabilities: ['PropertyIsEqualTo', 'BBOX', 'And', 'Or'],
    };
  }

  async getWMTSCapabilities(): Promise<MIWMTSCapabilities> {
    const url = this.buildUrl({
      SERVICE: 'WMTS',
      REQUEST: 'GetCapabilities',
      VERSION: '1.0.0',
    });
    const doc = await this.fetchXml(url);

    const version = doc.documentElement.getAttribute('version') || '1.0.0';
    const serviceEl = getAllByTag(doc, 'ServiceIdentification')[0];
    const title = serviceEl ? getTagText(serviceEl, 'Title') : '';

    const layers: MIWMTSLayerInfo[] = [];
    const layerEls = getAllByTag(doc, 'Layer');
    layerEls.forEach(el => {
      const identifier = getTagText(el, 'Identifier');
      if (!identifier) return;

      const formats: string[] = [];
      getAllByTag(el, 'Format').forEach(f => {
        if (f.textContent) formats.push(f.textContent.trim());
      });

      const tileMatrixSetLinks: string[] = [];
      getAllByTag(el, 'TileMatrixSetLink').forEach(link => {
        const tms = getTagText(link, 'TileMatrixSet');
        if (tms) tileMatrixSetLinks.push(tms);
      });

      const styles: { identifier: string; isDefault: boolean }[] = [];
      getAllByTag(el, 'Style').forEach(s => {
        styles.push({
          identifier: getTagText(s, 'Identifier'),
          isDefault: s.getAttribute('isDefault') === 'true',
        });
      });

      layers.push({
        identifier,
        title: getTagText(el, 'Title') || identifier,
        formats: formats.length > 0 ? formats : ['image/png'],
        tileMatrixSetLinks,
        styles,
        bbox: [0, 0, 0, 0],
      });
    });

    const tileMatrixSets: MITileMatrixSet[] = [];
    getAllByTag(doc, 'TileMatrixSet').forEach(tms => {
      const id = getTagText(tms, 'Identifier');
      if (!id) return;
      const supportedCRS = getTagText(tms, 'SupportedCRS');
      const matrices: MITileMatrixSet['tileMatrices'] = [];
      getAllByTag(tms, 'TileMatrix').forEach(tm => {
        matrices.push({
          identifier: getTagText(tm, 'Identifier'),
          scaleDenominator: parseFloat(getTagText(tm, 'ScaleDenominator')) || 0,
          topLeftCorner: (getTagText(tm, 'TopLeftCorner').split(' ').map(Number) as [number, number]) || [0, 0],
          tileWidth: parseInt(getTagText(tm, 'TileWidth')) || 256,
          tileHeight: parseInt(getTagText(tm, 'TileHeight')) || 256,
          matrixWidth: parseInt(getTagText(tm, 'MatrixWidth')) || 1,
          matrixHeight: parseInt(getTagText(tm, 'MatrixHeight')) || 1,
        });
      });
      tileMatrixSets.push({ identifier: id, supportedCRS, tileMatrices: matrices });
    });

    return { service: 'WMTS', version, title, layers, tileMatrixSets };
  }

  async getWCSCapabilities(): Promise<MIWCSCapabilities> {
    const url = this.buildUrl({
      SERVICE: 'WCS',
      REQUEST: 'GetCapabilities',
      VERSION: '2.0.1',
    });
    const doc = await this.fetchXml(url);

    const version = doc.documentElement.getAttribute('version') || '2.0.1';
    const serviceEl = getAllByTag(doc, 'ServiceIdentification')[0];
    const title = serviceEl ? getTagText(serviceEl, 'Title') : '';

    const coverages: MIWCSCoverageInfo[] = [];
    const covEls = getAllByTag(doc, 'CoverageSummary');
    covEls.forEach(el => {
      const id = getTagText(el, 'CoverageId') || getTagText(el, 'Identifier');
      if (!id) return;
      coverages.push({
        id,
        title: getTagText(el, 'Title') || id,
        subType: getTagText(el, 'CoverageSubtype') || 'RectifiedGridCoverage',
        bbox: [0, 0, 0, 0],
        crs: ['EPSG:4326'],
      });
    });

    return { service: 'WCS', version, title, coverages };
  }

  getWMSGetMapUrl(params: MIWMSGetMapParams): string {
    const urlParams: Record<string, string> = {
      SERVICE: 'WMS',
      REQUEST: 'GetMap',
      VERSION: this.version || '1.3.0',
      LAYERS: params.layers.join(','),
      BBOX: params.bbox.join(','),
      WIDTH: String(params.width),
      HEIGHT: String(params.height),
      CRS: params.crs || 'EPSG:4326',
      FORMAT: params.format || 'image/png',
      TRANSPARENT: String(params.transparent !== false),
    };
    if (params.styles) urlParams.STYLES = params.styles.join(',');
    if (params.cql_filter) urlParams.CQL_FILTER = params.cql_filter;
    if (params.time) urlParams.TIME = params.time;

    return this.resolveUrl(this.buildUrl(urlParams));
  }

  async getMap(params: MIWMSGetMapParams): Promise<Blob> {
    const url = this.getWMSGetMapUrl(params);
    const response = await fetch(url, { headers: this.getHeaders() });
    if (!response.ok) throw new Error(`GetMap failed: ${response.status}`);
    return response.blob();
  }

  async getFeatureInfo(params: MIWMSGetMapParams & { x: number; y: number; queryLayers: string[] }): Promise<any> {
    const urlParams: Record<string, string> = {
      SERVICE: 'WMS',
      REQUEST: 'GetFeatureInfo',
      VERSION: this.version || '1.3.0',
      LAYERS: params.layers.join(','),
      QUERY_LAYERS: params.queryLayers.join(','),
      BBOX: params.bbox.join(','),
      WIDTH: String(params.width),
      HEIGHT: String(params.height),
      CRS: params.crs || 'EPSG:4326',
      INFO_FORMAT: 'application/json',
      I: String(params.x),
      J: String(params.y),
    };
    const url = this.buildUrl(urlParams);
    return this.fetchJson(url);
  }

  async getFeature(params: MIWFSGetFeatureParams): Promise<any> {
    const urlParams: Record<string, string> = {
      SERVICE: 'WFS',
      REQUEST: 'GetFeature',
      VERSION: this.version || '2.0.0',
      TYPENAMES: params.typeName,
      OUTPUTFORMAT: params.outputFormat || 'application/json',
    };
    if (params.maxFeatures) urlParams.COUNT = String(params.maxFeatures);
    if (params.startIndex) urlParams.STARTINDEX = String(params.startIndex);
    if (params.bbox) urlParams.BBOX = params.bbox.join(',');
    if (params.srsName) urlParams.SRSNAME = params.srsName;
    if (params.cql_filter) urlParams.CQL_FILTER = params.cql_filter;
    if (params.propertyName) urlParams.PROPERTYNAME = params.propertyName.join(',');
    if (params.sortBy) urlParams.SORTBY = params.sortBy;

    const url = this.buildUrl(urlParams);
    return this.fetchJson(url);
  }

  async getCollections(): Promise<MIOGCCollection[]> {
    const url = `${this.url}/collections`;
    const data = await this.fetchJson(url);
    return data.collections || [];
  }

  async getCollection(collectionId: string): Promise<MIOGCCollection> {
    const url = `${this.url}/collections/${collectionId}`;
    return this.fetchJson(url);
  }

  async getItems(collectionId: string, params?: MIOGCItemsParams): Promise<any> {
    let url = `${this.url}/collections/${collectionId}/items`;
    if (params) {
      const searchParams = new URLSearchParams();
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.offset) searchParams.set('offset', String(params.offset));
      if (params.bbox) searchParams.set('bbox', params.bbox.join(','));
      if (params.datetime) searchParams.set('datetime', params.datetime);
      if (params.filter) searchParams.set('filter', params.filter);
      const qs = searchParams.toString();
      if (qs) url += `?${qs}`;
    }
    return this.fetchJson(url);
  }

  getServerInfo(): MIServerEntry {
    return {
      name: 'OGC Server',
      url: this.url,
      type: 'ogc-wms',
      auth: this.auth,
      proxy: this.proxy,
    };
  }

  isServiceAvailable(service: string): boolean {
    const url = this.url.toLowerCase();
    if (service === 'WMS' && (url.includes('wms') || url.includes('geoserver'))) return true;
    if (service === 'WFS' && (url.includes('wfs') || url.includes('geoserver'))) return true;
    if (service === 'WMTS' && url.includes('wmts')) return true;
    return true;
  }

  getSupportedCRS(): string[] {
    return ['EPSG:4326', 'EPSG:3857', 'EPSG:900913'];
  }

  getSupportedFormats(service: string): string[] {
    switch (service) {
      case 'WMS': return ['image/png', 'image/jpeg', 'image/gif'];
      case 'WFS': return ['application/json', 'application/gml+xml', 'text/xml'];
      case 'WMTS': return ['image/png', 'image/jpeg'];
      default: return [];
    }
  }

  createWMSTileUrl(layerNames: string | string[]): string {
    const layers = Array.isArray(layerNames) ? layerNames.join(',') : layerNames;
    const params: Record<string, string> = {
      SERVICE: 'WMS',
      REQUEST: 'GetMap',
      VERSION: this.version || '1.3.0',
      LAYERS: layers,
      CRS: 'EPSG:3857',
      FORMAT: 'image/png',
      TRANSPARENT: 'true',
      WIDTH: '256',
      HEIGHT: '256',
      BBOX: '{bbox-epsg-3857}',
    };
    const url = new URL(this.url);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    return this.resolveUrl(url.toString());
  }
}
